import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import { LatLngLiteral } from "leaflet";

import {
  HvGlobalActions,
  HvTypography,
  HvGrid,
  HvInput,
  HvLoading,
} from "@hitachivantara/uikit-react-core";

import {
  IconData,
  geocodeLatLng,
  geocodeAddress,
  useEntityTypeCamera,
} from "lib/api";

import GenericMap from "components/common/GenericMap";

import useStyles from "./styles";

const Location: React.FC = () => {
  const { t } = useTranslation("cameraAddEdit");
  const classes = useStyles();

  const { values, setValues, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<CameraFormData>();

  const parseStatus = (field: keyof CameraFormData, untouched = false) => {
    return errors[field] && (untouched || touched[field]) ? "invalid" : "valid";
  };

  const parseStatusMessage = (
    field: keyof CameraFormData,
    untouched = false
  ) => {
    return errors[field] && (untouched || touched[field]) ? errors[field] : "";
  };

  const { data: typeData } = useEntityTypeCamera();

  const [icon, setIcon] = useState<IconData | undefined>();
  const [location, setLocation] = useState<LatLngLiteral | undefined>(
    undefined
  );

  useEffect(() => {
    const type = typeData?.data;
    // shape properties no set in db but we want circle marker for camera
    setIcon({ ...type?.appearance, shape: "circle" } as IconData);
  }, [typeData?.data, values.type]);

  const onChange = useCallback(
    async (newLoc: LatLngLiteral) => {
      setValues((existing) => ({
        ...existing,
        latitude: newLoc.lat,
        longitude: newLoc.lng,
      }));
      setFieldTouched("latitude");
      setFieldTouched("longitude");
    },
    [setFieldTouched, setValues]
  );

  const onSearch = useCallback(
    async (search: string) => {
      const geocode = await geocodeAddress(search);
      setValues((existing) => ({
        ...existing,
        latitude: geocode?.data.coordinates.lat,
        longitude: geocode?.data.coordinates.lng,
      }));
      setFieldTouched("latitude");
      setFieldTouched("longitude");
    },
    [setFieldTouched, setValues]
  );

  useEffect(() => {
    const lat = Number(values.latitude);
    const lng = Number(values.longitude);
    if (
      (values.latitude as unknown) !== "" &&
      !Number.isNaN(lat) &&
      (values.longitude as unknown) !== "" &&
      !Number.isNaN(lng)
    ) {
      setLocation({
        lat,
        lng,
      });
    } else {
      setLocation(undefined);
    }

    if (
      (values.latitude as unknown) === "" ||
      Number.isNaN(lat) ||
      (values.longitude as unknown) === "" ||
      Number.isNaN(lng)
    ) {
      setFieldValue("address", "");
    }
  }, [setFieldValue, values.latitude, values.longitude]);

  const [loadingAddress, setLoadingAddress] = useState(false);
  useEffect(() => {
    if (!location) return;

    const getAddress = async () => {
      try {
        const geocode = await geocodeLatLng(location);
        setFieldValue("address", geocode?.data.formattedAddress ?? "");
      } catch (e) {
        setFieldValue("address", "");
      }
      setLoadingAddress(false);
    };
    if (touched.latitude || touched.longitude) {
      setLoadingAddress(true);
      setFieldValue("address", "");
      getAddress();
    }
  }, [location, setFieldValue, touched.latitude, touched.longitude]);

  const latitudeFilledIn = (values.latitude ?? "") !== "";
  const longitudeFilledIn = (values.longitude ?? "") !== "";

  return (
    <HvGrid container className={classes.root}>
      <HvGrid sm={12} item>
        <HvGlobalActions title={t("location.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("location.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid container item xs={12} lg={9}>
        <HvGrid xs={12} md={6} item>
          <GenericMap
            clickToMoveMarker
            dragToMoveMarker
            onChange={onChange}
            onSearch={onSearch}
            marker={icon}
            location={location}
            searchPlaceholder={t("location.map.searchPlaceholder")}
            searchButtonLabel={t("location.map.searchButtonLabel")}
            clearButtonLabel={t("location.map.clearButtonLabel")}
          />
        </HvGrid>
        <HvGrid container item xs={12} md={6} alignContent="flex-start">
          <HvGrid xs={12} className={classes.gridPaddingOverRide}>
            <HvInput
              className={classes.input}
              label={t("location.fields.address")}
              readOnly
              placeholder={loadingAddress ? "" : "—"}
              value={loadingAddress ? "" : values.address ?? ""}
              status={loadingAddress ? "valid" : parseStatus("address", true)}
              statusMessage={parseStatusMessage("address", true)}
              endAdornment={loadingAddress && <HvLoading small />}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
            <HvInput
              className={classes.input}
              label={t("location.fields.latitude")}
              description={t("location.fields.latitudeDescription")}
              placeholder={
                longitudeFilledIn
                  ? ""
                  : t("location.fields.latitudePlaceholder")
              }
              inputProps={{
                autoComplete: "off",
              }}
              value={values.latitude?.toString() ?? ""}
              required={longitudeFilledIn}
              status={parseStatus("latitude")}
              statusMessage={parseStatusMessage("latitude")}
              onChange={(evt, value) => {
                setFieldValue("latitude", value);
              }}
              onBlur={() => {
                setFieldTouched("latitude");
              }}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
            <HvInput
              className={classes.input}
              label={t("location.fields.longitude")}
              description={t("location.fields.longitudeDescription")}
              placeholder={
                latitudeFilledIn
                  ? ""
                  : t("location.fields.longitudePlaceholder")
              }
              value={values.longitude?.toString() ?? ""}
              required={latitudeFilledIn}
              status={parseStatus("longitude")}
              statusMessage={parseStatusMessage("longitude")}
              onChange={(evt, value) => {
                setFieldValue("longitude", value);
              }}
              onBlur={() => {
                setFieldTouched("longitude");
              }}
            />
          </HvGrid>
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default Location;
